/**
 * "Työ valmis?" (#4101) — shown when the last open labour step or service is
 * completed, i.e. the action that moves the work order to `work_completed`.
 *
 * One dialog confirms the work is done and collects what the hand-back needs:
 * the odometer reading (a new record, at least the latest one), where the car
 * was left (the #2341 parking grid) and — when the tenant uses service books —
 * whether the book entry was made (#2340). Submitting writes them through the
 * existing endpoints and only then completes the step. Cancel leaves the step
 * open.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ParkingSpotSelector } from '@/components/workorders/ParkingSpotSelector';
import { useRecordWorkOrderMileage, useUpdateVehicleLocation } from '@/hooks/useWorkOrders';
import { useLatestMileage } from '@/hooks/useVehicles';
import { useParkingCurrentStatus } from '@/hooks/useParking';
import { useUpdateServiceBook } from '@/hooks/useChecklists';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import { formatNumber } from '@/utils/locale';

export interface WorkCompletionWorkOrder {
  id: string;
  vehicleId: string;
  vehicleLocation: string | null;
  vehicleLocationDetail: string | null;
  serviceBookFilled?: boolean | undefined;
}

export interface WorkCompletionDialogProps {
  open: boolean;
  workOrder: WorkCompletionWorkOrder;
  /** Completes the step; throws on failure so the dialog stays open. */
  complete: () => Promise<void>;
  onCancel: () => void;
  onCompleted: () => void;
}

type ServiceBookAnswer = 'yes' | 'no' | '';

interface RecordedSteps {
  mileage: number | null;
  spotId: string | null;
  serviceBook: ServiceBookAnswer;
}

const EMPTY_RECORDED: RecordedSteps = { mileage: null, spotId: null, serviceBook: '' };

export function WorkCompletionDialog({
  open,
  workOrder,
  complete,
  onCancel,
  onCompleted,
}: WorkCompletionDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { serviceBookEnabled } = useTenantSettings();
  const latestMileageQuery = useLatestMileage(workOrder.vehicleId);
  const parkingQuery = useParkingCurrentStatus();
  const recordMileage = useRecordWorkOrderMileage();
  const updateLocation = useUpdateVehicleLocation();
  const updateServiceBook = useUpdateServiceBook();

  const [mileageInput, setMileageInput] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [spotId, setSpotId] = useState('');
  const [keepCurrentSpot, setKeepCurrentSpot] = useState(false);
  const [serviceBookAnswer, setServiceBookAnswer] = useState<ServiceBookAnswer>('');
  const [submitting, setSubmitting] = useState(false);
  // What a failed attempt already wrote, so a retry does not record it twice.
  const recorded = useRef<RecordedSteps>(EMPTY_RECORDED);

  useEffect(() => {
    if (!open) return;
    setMileageInput('');
    setZoneId('');
    setSpotId('');
    setKeepCurrentSpot(false);
    setServiceBookAnswer('');
    setSubmitting(false);
    recorded.current = EMPTY_RECORDED;
  }, [open]);

  const latest = latestMileageQuery.data?.mileage != null && latestMileageQuery.data.recordedAt
    ? { mileage: latestMileageQuery.data.mileage, recordedAt: latestMileageQuery.data.recordedAt }
    : null;
  const enteredMileage = mileageInput === '' ? null : Number(mileageInput);
  const mileageBelowLatest = enteredMileage != null && latest != null && enteredMileage < latest.mileage;
  const mileageOk = enteredMileage != null && Number.isInteger(enteredMileage) && !mileageBelowLatest
    && !latestMileageQuery.isLoading;

  const hasParkingSpots = (parkingQuery.data?.zones ?? []).some((zone) => zone.spots.length > 0);
  const isParked = workOrder.vehicleLocation === 'parking';
  // Without parking spots (or when they cannot be loaded) there is nothing to
  // pick, and the step is skipped rather than blocking the completion.
  const parkingOk = !parkingQuery.isLoading
    && (!hasParkingSpots || (isParked && keepCurrentSpot) || (zoneId !== '' && spotId !== ''));

  const askServiceBook = serviceBookEnabled && workOrder.serviceBookFilled !== true;
  const serviceBookOk = !askServiceBook || serviceBookAnswer !== '';

  const canSubmit = mileageOk && parkingOk && serviceBookOk && !submitting;

  const currentLocationLabel = workOrder.vehicleLocation
    ? [t(`vehicleLocation.${workOrder.vehicleLocation}`), workOrder.vehicleLocationDetail]
        .filter(Boolean)
        .join(' • ')
    : '';

  const handleSubmit = async () => {
    if (!canSubmit || enteredMileage == null) return;
    setSubmitting(true);
    try {
      if (recorded.current.mileage !== enteredMileage) {
        await recordMileage.mutateAsync({
          id: workOrder.id,
          vehicleId: workOrder.vehicleId,
          mileage: enteredMileage,
        });
        recorded.current = { ...recorded.current, mileage: enteredMileage };
      }
      const moveToSpot = hasParkingSpots && !(isParked && keepCurrentSpot) && spotId !== '';
      if (moveToSpot && recorded.current.spotId !== spotId) {
        await updateLocation.mutateAsync({
          id: workOrder.id,
          data: { locationType: 'parking', locationZoneId: zoneId, locationSpotId: spotId },
        });
        recorded.current = { ...recorded.current, spotId };
      }
      if (askServiceBook && serviceBookAnswer !== '' && recorded.current.serviceBook !== serviceBookAnswer) {
        await updateServiceBook.mutateAsync({
          workOrderId: workOrder.id,
          serviceBookChecked: true,
          serviceBookFilled: serviceBookAnswer === 'yes',
        });
        recorded.current = { ...recorded.current, serviceBook: serviceBookAnswer };
      }
      await complete();
      onCompleted();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('workCompletion.failed') }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={submitting ? () => undefined : onCancel}
      mobileTitle={t('workCompletion.title')}
      maxWidth={hasParkingSpots ? 'md' : 'sm'}
      fullWidth
    >
      <DialogTitle>{t('workCompletion.title')}</DialogTitle>
      <DialogContent data-testid="work-completion-dialog">
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            {t('workCompletion.message')}
          </Typography>

          <TextField
            label={t('workCompletion.mileageLabel')}
            placeholder={t('create.mileagePlaceholder')}
            value={mileageInput}
            onChange={(event) => setMileageInput(event.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            required
            fullWidth
            size="small"
            error={mileageBelowLatest}
            inputProps={{ 'data-testid': 'work-completion-mileage' }}
            helperText={
              mileageBelowLatest && latest
                ? t('workCompletion.mileageBelowLatest', { latest: formatNumber(latest.mileage) })
                : latest
                  ? t('workCompletion.mileageHintLatest', {
                      mileage: formatNumber(latest.mileage),
                      date: format(new Date(latest.recordedAt), 'd.M.yyyy'),
                    })
                  : t('workCompletion.mileageHintNone')
            }
          />

          {hasParkingSpots && (
            <Box>
              <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>
                {t('workCompletion.parkingTitle')}
              </Typography>
              {isParked && (
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={(
                    <Checkbox
                      checked={keepCurrentSpot}
                      onChange={(event) => setKeepCurrentSpot(event.target.checked)}
                      inputProps={{ 'aria-label': t('workCompletion.keepCurrentSpot', { location: currentLocationLabel }) }}
                      data-testid="work-completion-keep-spot"
                    />
                  )}
                  label={t('workCompletion.keepCurrentSpot', { location: currentLocationLabel })}
                />
              )}
              {!(isParked && keepCurrentSpot) && (
                <ParkingSpotSelector
                  selectedZoneId={zoneId}
                  selectedSpotId={spotId}
                  onZoneChange={setZoneId}
                  onSpotChange={setSpotId}
                />
              )}
            </Box>
          )}

          {askServiceBook && (
            <FormControl>
              <FormLabel id="work-completion-service-book">{t('serviceBook.fillPromptTitle')}</FormLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('serviceBook.fillPromptMessage')}
              </Typography>
              <RadioGroup
                row
                aria-labelledby="work-completion-service-book"
                value={serviceBookAnswer}
                onChange={(event) => setServiceBookAnswer(event.target.value as ServiceBookAnswer)}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio data-testid="work-completion-service-book-yes" />}
                  label={t('serviceBook.fillYes')}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio data-testid="work-completion-service-book-no" />}
                  label={t('serviceBook.fillNo')}
                />
              </RadioGroup>
            </FormControl>
          )}

          {!canSubmit && !submitting && (
            <Alert severity="info" variant="outlined">
              {t('workCompletion.requiredHint')}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => { void handleSubmit(); }}
          disabled={!canSubmit}
          data-testid="work-completion-submit"
          {...(submitting ? { startIcon: <CircularProgress size={16} /> } : {})}
        >
          {t('workCompletion.submit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
